/**
 * Bot message parser: raw Mattermost text → structured data for BotMessageCard.
 *
 * Handles:
 * - Emoji shortcodes → Unicode
 * - Markdown bold (**text**)
 * - Key-value fields ("Ключ: Значение" and "| Ключ | Значение |")
 * - Numbered order lists
 * - Nested warnings
 * - Message type classification → accent color + icon
 * - Payer line «Плательщик: <компания> (корп.)» (contract v2.0 §9.2) and unrecognised lines (WP-DSP-FIX-01)
 */
/* ── Emoji shortcode → Unicode map ── */
const EMOJI_MAP = {
    ":new:": "🆕",
    ":warning:": "⚠️",
    ":white_check_mark:": "✅",
    ":truck:": "🚚",
    ":round_pushpin:": "📍",
    ":zap:": "⚡",
    ":rocket:": "🚀",
    ":checkered_flag:": "🏁",
    ":clipboard:": "📋",
    ":no_entry_sign:": "🚫",
    ":speech_balloon:": "💬",
    ":raising_hand:": "🙋",
    ":paperclip:": "📎",
    ":hourglass:": "⏳",
    ":bell:": "🔔",
    ":memo:": "📝",
    ":wrench:": "🔧",
    ":rotating_light:": "🚨",
    ":x:": "❌",
    ":check:": "✅",
    ":heavy_check_mark:": "✅",
    ":exclamation:": "❗",
    ":question:": "❓",
    ":star:": "⭐",
    ":fire:": "🔥",
    ":gear:": "⚙️",
    ":link:": "🔗",
    ":calendar:": "📅",
    ":clock:": "🕐",
    ":phone:": "📞",
    ":email:": "📧",
    ":thumbsup:": "👍",
    ":thumbsdown:": "👎",
    ":red_circle:": "🔴",
    ":green_circle:": "🟢",
    ":large_blue_circle:": "🔵",
};
export function replaceEmoji(text) {
    return text.replace(/:[a-z0-9_]+:/g, (match) => EMOJI_MAP[match] ?? match);
}
/* ── Accent colors ── */
export var AccentColor;
(function (AccentColor) {
    AccentColor["GREEN"] = "green";
    AccentColor["BLUE"] = "blue";
    AccentColor["GOLD"] = "gold";
    AccentColor["RED"] = "red";
    AccentColor["ORANGE"] = "orange";
    AccentColor["GRAY"] = "gray";
})(AccentColor || (AccentColor = {}));
/* ── Message type ── */
export var BotMessageType;
(function (BotMessageType) {
    BotMessageType["NEW_ORDER"] = "new_order";
    BotMessageType["ORDER_ASSIGNED"] = "order_assigned";
    BotMessageType["TECHNICIAN_EN_ROUTE"] = "en_route";
    BotMessageType["TECHNICIAN_ARRIVED"] = "arrived";
    BotMessageType["CHARGING_STARTED"] = "charging";
    BotMessageType["ORDER_COMPLETED"] = "order_completed";
    BotMessageType["ROUTE_STARTED"] = "route_started";
    BotMessageType["ROUTE_COMPLETED"] = "route_completed";
    BotMessageType["ROUTE_CREATED"] = "route_created";
    BotMessageType["ROUTE_CANCELLED"] = "route_cancelled";
    BotMessageType["ORDER_THREAD"] = "order_thread";
    BotMessageType["UNROUTED_ORDER"] = "unrouted_order";
    BotMessageType["ORDER_COMPLETED_WITH_WARNING"] = "order_completed_warning";
    BotMessageType["SYSTEM_WARNING"] = "system_warning";
    /** Mattermost system messages: "joined the channel", "added to the channel", etc. */
    BotMessageType["SYSTEM_JOIN"] = "system_join";
    BotMessageType["GENERIC"] = "generic";
})(BotMessageType || (BotMessageType = {}));
/** Detect Mattermost system messages that should not be rendered as rich cards */
const SYSTEM_JOIN_PATTERNS = [
    /joined the channel/i,
    /added to the channel/i,
    /removed from the channel/i,
    /left the channel/i,
    /updated the channel/i,
    /was added to the team/i,
];
const TYPE_RULES = [
    {
        type: BotMessageType.UNROUTED_ORDER,
        accent: AccentColor.RED,
        icon: "⚠️",
        title: "Заказ без маршрута",
        match: (t) => t.includes(":warning:") && /Заказ без маршрута/i.test(t),
    },
    {
        type: BotMessageType.ORDER_COMPLETED_WITH_WARNING,
        accent: AccentColor.GREEN,
        icon: "✅",
        title: "Заказ выполнен",
        match: (t) => t.includes(":warning:") && t.includes(":white_check_mark:") && /выполнен/i.test(t),
    },
    {
        type: BotMessageType.NEW_ORDER,
        accent: AccentColor.GREEN,
        icon: "🆕",
        title: "Новый заказ",
        match: (t) => t.includes(":new:") && /Новый заказ/i.test(t),
    },
    {
        type: BotMessageType.ORDER_ASSIGNED,
        accent: AccentColor.BLUE,
        icon: "✅",
        title: "Заказ назначен",
        match: (t) => t.includes(":white_check_mark:") && /назначен/i.test(t),
    },
    {
        type: BotMessageType.TECHNICIAN_EN_ROUTE,
        accent: AccentColor.BLUE,
        icon: "🚚",
        title: "Техник выехал",
        match: (t) => t.includes(":truck:"),
    },
    {
        type: BotMessageType.TECHNICIAN_ARRIVED,
        accent: AccentColor.BLUE,
        icon: "📍",
        title: "Техник на месте",
        match: (t) => t.includes(":round_pushpin:"),
    },
    {
        type: BotMessageType.CHARGING_STARTED,
        accent: AccentColor.GOLD,
        icon: "⚡",
        title: "Зарядка начата",
        match: (t) => t.includes(":zap:"),
    },
    {
        type: BotMessageType.ORDER_COMPLETED,
        accent: AccentColor.GREEN,
        icon: "✅",
        title: "Заказ выполнен",
        match: (t) => t.includes(":white_check_mark:") && /выполнен/i.test(t),
    },
    {
        type: BotMessageType.ROUTE_COMPLETED,
        accent: AccentColor.GREEN,
        icon: "🏁",
        title: "Маршрут завершён",
        match: (t) => t.includes(":checkered_flag:"),
    },
    {
        type: BotMessageType.ROUTE_STARTED,
        accent: AccentColor.BLUE,
        icon: "🚀",
        title: "Маршрут начат",
        match: (t) => t.includes(":rocket:"),
    },
    {
        type: BotMessageType.ROUTE_CREATED,
        accent: AccentColor.BLUE,
        icon: "📋",
        title: "Маршрут создан",
        match: (t) => t.includes(":clipboard:"),
    },
    {
        type: BotMessageType.ROUTE_CANCELLED,
        accent: AccentColor.RED,
        icon: "🚫",
        title: "Маршрут отменён",
        match: (t) => t.includes(":no_entry_sign:"),
    },
    {
        type: BotMessageType.ORDER_THREAD,
        accent: AccentColor.BLUE,
        icon: "💬",
        title: "Чат с клиентом",
        match: (t) => t.includes(":speech_balloon:"),
    },
    {
        type: BotMessageType.SYSTEM_WARNING,
        accent: AccentColor.ORANGE,
        icon: "⚠️",
        title: "Предупреждение",
        match: (t) => t.includes(":warning:"),
    },
];
function classify(raw) {
    // Detect system join/leave messages first — they should not be rich cards
    if (SYSTEM_JOIN_PATTERNS.some((p) => p.test(raw))) {
        return { type: BotMessageType.SYSTEM_JOIN, accent: AccentColor.GRAY, icon: "", title: "" };
    }
    for (const rule of TYPE_RULES) {
        if (rule.match(raw)) {
            return { type: rule.type, accent: rule.accent, icon: rule.icon, title: rule.title };
        }
    }
    return { type: BotMessageType.GENERIC, accent: AccentColor.GRAY, icon: "📢", title: "Уведомление" };
}
/* ── Field extraction ── */
/** Метки полей, которые карточка показывает в сетке «метка — значение». */
const FIELD_LABELS = "Адрес|Дата|Статус|Техник|Заказ|Клиент|Телефон|Маршрут|Заказов|Выполнено заказов";
const FIELD_LINE = new RegExp(`^\\s*[-–—]?\\s*(?:${FIELD_LABELS})\\s*[:：]\\s*\\S`, "i");
const PAYER_LINE = /^\s*[-–—]?\s*Плательщик\s*[:：]\s*(.+?)\s*$/i;
const CORP_MARK = /\s*\(\s*корп\.?\s*\)\s*$/i;
const HEADER_LINE = /^(?::[a-z0-9_]+:\s*)+\*\*[^*]+\*\*/;
/** Жирный подзаголовок списка: «**Заказы:**», «**Итоги по заказам:**» — список под ним показывается */
const SECTION_HEADING_LINE = /^\*\*[^*]+:\*\*$/;
/** Номер заказа: клиентское приложение — «PZ-0748», Диспетчер — «ORD-20260921-014» */
const ORDER_NUMBER = /\b(?:PZ-\d+|ORD-\d{8}-\d+)\b/;
/** Extract "– Label: Value" or "Label: Value" lines */
function extractDashFields(text) {
    const fields = [];
    // Match lines like "- Адрес: value" or "– Адрес: value" or standalone "Адрес: value"
    const linePattern = new RegExp(`(?:^|\\n)\\s*[-–—]?\\s*(${FIELD_LABELS})\\s*[:：]\\s*(.+)`, "gi");
    let m;
    while ((m = linePattern.exec(text)) !== null) {
        const label = m[1].trim();
        const value = m[2].trim().replace(/\*\*/g, "");
        if (value)
            fields.push({ label, value });
    }
    return fields;
}
/**
 * WP-DSP-FIX-01: «Плательщик: QA-CORP-A (корп.)» (контракт v2.0 §9.2). Жирное оформление строки допускается.
 * Раньше строка не входила в список меток и терялась — диспетчер не видел, что заказ корпоративный.
 */
function extractPayer(text) {
    for (const line of text.split("\n")) {
        const m = line.replace(/\*\*/g, "").match(PAYER_LINE);
        if (!m)
            continue;
        const value = m[1].trim();
        const corporate = CORP_MARK.test(value);
        const name = value.replace(CORP_MARK, "").trim();
        return { name: name || value, corporate };
    }
    return null;
}
/**
 * WP-DSP-FIX-01: строки, которые не стали заголовком, полем, плательщиком, списком заказов, предупреждением,
 * подсказкой или заметкой. Их показываем текстом — новые строки Бота не должны пропадать молча.
 */
function extractExtraLines(raw, type) {
    const extra = [];
    // У нераспознанного поста первая строка — не заголовок карточки («Уведомление»), её тоже показываем
    let firstLineSeen = type === BotMessageType.GENERIC;
    for (const line of raw.split("\n")) {
        const trimmed = line.trim();
        if (!trimmed)
            continue;
        const plain = trimmed.replace(/\*\*/g, "");
        const recognised = !firstLineSeen || // заголовок поста
            HEADER_LINE.test(trimmed) ||
            SECTION_HEADING_LINE.test(trimmed) ||
            FIELD_LINE.test(plain) ||
            PAYER_LINE.test(plain) ||
            /^\|.*\|$/.test(trimmed) || // строка таблицы
            /^\d+\.\s*\*\*/.test(trimmed) || // список заказов
            (trimmed.startsWith(":warning:") && !trimmed.includes("**")) ||
            /^Назначьте\s/i.test(plain) ||
            trimmed.includes(":hourglass:");
        firstLineSeen = true;
        if (recognised)
            continue;
        const cleaned = replaceEmoji(plain).replace(/^[-–—]\s*/, "").trim();
        if (cleaned)
            extra.push(cleaned);
    }
    return extra;
}
/** Extract markdown table: | Key | Value | */
function extractTableFields(text) {
    const fields = [];
    // Match table rows: | key | value |
    const rowPattern = /\|\s*([^|]+?)\s*\|\s*([^|]+?)\s*\|/g;
    let m;
    while ((m = rowPattern.exec(text)) !== null) {
        const label = m[1].trim();
        const value = m[2].trim();
        // Skip header separator rows (---) and header row (Поле/Значение)
        if (/^[-–—:]+$/.test(label) || /^[-–—:]+$/.test(value))
            continue;
        if (label === "Поле" && value === "Значение")
            continue;
        if (label && value)
            fields.push({ label, value });
    }
    return fields;
}
/** Extract numbered order list: 1. **PZ-0001** — address (status) */
function extractOrderList(text) {
    const items = [];
    const pattern = /\d+\.\s*\*\*([^*]+)\*\*\s*[-–—]\s*([^(\n]+?)(?:\(([^)]+)\))?$/gm;
    let m;
    while ((m = pattern.exec(text)) !== null) {
        items.push({
            number: m[1].trim(),
            address: m[2].trim(),
            status: m[3]?.trim(),
        });
    }
    return items;
}
/** Extract wait time: "(ожидает 37 мин)" */
function extractWaitMinutes(text) {
    const m = text.match(/\(ожидает\s+(\d+)\s*мин/i);
    return m ? parseInt(m[1], 10) : null;
}
/** Extract warning lines */
function extractWarnings(text) {
    const warnings = [];
    // Lines with :warning: or [text in brackets]
    const lines = text.split("\n");
    for (const line of lines) {
        const trimmed = line.trim();
        // Standalone warning lines like ":warning: [канал маршрута не найден]"
        if (trimmed.startsWith(":warning:") && !trimmed.includes("**")) {
            const clean = replaceEmoji(trimmed).replace(/^\s*⚠️\s*/, "").trim();
            // Remove brackets
            const noBrackets = clean.replace(/^\[/, "").replace(/\]$/, "");
            if (noBrackets)
                warnings.push(noBrackets);
        }
        // Bracketed warnings inside other lines
        const bracketMatch = trimmed.match(/\[([^\]]+)\]/g);
        if (bracketMatch && !trimmed.startsWith(":warning:")) {
            for (const bm of bracketMatch) {
                const inner = bm.slice(1, -1).trim();
                if (inner && /не найден|ошибк|внимани/i.test(inner)) {
                    warnings.push(inner);
                }
            }
        }
    }
    return warnings;
}
/** Extract CTA text like "Назначьте маршрут в Диспетчерской" */
function extractCta(text) {
    const m = text.match(/(?:^|\n)\s*(Назначьте\s+.+?)(?:\.|$)/im);
    return m ? m[1].trim() : null;
}
/** Extract :hourglass: note */
function extractNote(text) {
    const m = text.match(/:hourglass:\s*(.+)/i);
    if (m)
        return replaceEmoji(`:hourglass: ${m[1].trim()}`);
    return null;
}
/** Extract order number from bold: **Новый заказ** PZ-0369 or Заказ: PZ-0369 (also ORD-… from the dispatcher) */
function extractOrderNumber(text) {
    const m = text.match(ORDER_NUMBER);
    return m ? m[0] : null;
}
/** Build a better title from the message */
function buildTitle(raw, baseTitle, type) {
    const orderNum = extractOrderNumber(raw);
    switch (type) {
        case BotMessageType.NEW_ORDER:
        case BotMessageType.ORDER_ASSIGNED:
        case BotMessageType.ORDER_COMPLETED:
        case BotMessageType.ORDER_COMPLETED_WITH_WARNING:
            return orderNum ? `${baseTitle} ${orderNum}` : baseTitle;
        case BotMessageType.TECHNICIAN_EN_ROUTE:
        case BotMessageType.TECHNICIAN_ARRIVED:
        case BotMessageType.CHARGING_STARTED:
            return orderNum ? `${baseTitle} · ${orderNum}` : baseTitle;
        case BotMessageType.UNROUTED_ORDER: {
            const wait = extractWaitMinutes(raw);
            if (orderNum && wait)
                return `${baseTitle} · ${orderNum}`;
            if (orderNum)
                return `${baseTitle} · ${orderNum}`;
            return baseTitle;
        }
        case BotMessageType.ROUTE_CREATED:
        case BotMessageType.ROUTE_STARTED:
        case BotMessageType.ROUTE_COMPLETED:
        case BotMessageType.ROUTE_CANCELLED: {
            // Try to extract route short id
            const routeMatch = raw.match(/Маршрут\s*[:：]\s*([a-f0-9]+)/i);
            return routeMatch ? `${baseTitle} · ${routeMatch[1]}` : baseTitle;
        }
        case BotMessageType.ORDER_THREAD:
            return orderNum ? `Чат с клиентом по ${orderNum}` : baseTitle;
        default:
            return baseTitle;
    }
}
/* ── Main parser ── */
export function parseBotMessage(raw) {
    const { type, accent, icon, title: baseTitle } = classify(raw);
    const title = buildTitle(raw, baseTitle, type);
    const waitMinutes = extractWaitMinutes(raw);
    // Extract fields: prefer table format, fallback to dash-colon format
    const tableFields = extractTableFields(raw);
    const dashFields = extractDashFields(raw);
    const fields = tableFields.length > 0 ? tableFields : dashFields;
    const orderList = extractOrderList(raw);
    const warnings = extractWarnings(raw);
    const cta = extractCta(raw);
    const note = extractNote(raw);
    const payer = type === BotMessageType.SYSTEM_JOIN ? null : extractPayer(raw);
    // Build remaining text: anything that wasn't captured as fields/lists/warnings
    let remainingText = null;
    if (type === BotMessageType.GENERIC || type === BotMessageType.SYSTEM_WARNING) {
        // For generic/warning messages, show the cleaned text.
        // Строка плательщика показывается отдельной секцией — в общем тексте её не дублируем (WP-DSP-FIX-01)
        const withoutPayer = payer
            ? raw
                .split("\n")
                .filter((line) => !PAYER_LINE.test(line.replace(/\*\*/g, "")))
                .join("\n")
            : raw;
        const cleaned = replaceEmoji(withoutPayer)
            .replace(/\*\*/g, "")
            .trim();
        if (cleaned && fields.length === 0 && orderList.length === 0) {
            remainingText = cleaned;
        }
    }
    // Весь текст уже показан (remainingText) или это системное сообщение — отдельные строки не нужны
    const extraLines = remainingText !== null || type === BotMessageType.SYSTEM_JOIN ? [] : extractExtraLines(raw, type);
    return {
        type,
        accent,
        icon,
        title,
        fields,
        orderList,
        warnings,
        cta,
        note,
        remainingText,
        waitMinutes,
        orderNumber: extractOrderNumber(raw),
        payer,
        extraLines,
    };
}
